/**
 * Helpers for working with <maitai_reasoning> tags in completions.
 * 
 * We use a model-agnostic tag format so reasoning can be stored consistently,
 * then swapped out for model-specific tags when needed for finetuning.
 */

export const TAG_OPEN = "<maitai_reasoning>";
export const TAG_CLOSE = "</maitai_reasoning>";

export interface ReasoningData {
  [key: string]: string;
}

export function hasReasoning(text: string): boolean {
  return text.includes(TAG_OPEN) && text.includes(TAG_CLOSE);
}

export function getReasoningContent(text: string): string | null {
  const openIdx = text.indexOf(TAG_OPEN);
  if (openIdx === -1) return null;

  const closeIdx = text.indexOf(TAG_CLOSE, openIdx + TAG_OPEN.length);
  if (closeIdx === -1) return null;

  return text.substring(openIdx + TAG_OPEN.length, closeIdx).trim();
}

export function stripReasoning(text: string): string {
  if (!hasReasoning(text)) return text;

  const openIdx = text.indexOf(TAG_OPEN);
  const closeIdx = text.indexOf(TAG_CLOSE, openIdx);
  if (closeIdx === -1) return text;

  const prefix = text.substring(0, openIdx).trim();
  const suffix = text.substring(closeIdx + TAG_CLOSE.length).trim();

  if (prefix && suffix) return `${prefix}\n${suffix}`;
  return prefix || suffix;
}

export function parseKV(content: string): ReasoningData {
  const out: ReasoningData = {};

  // Check for XML-style <field name="...">value</field> first
  const fieldRegex = /<field\s+name="([^"]+)">\s*([\s\S]*?)\s*<\/field>/g;
  let match;
  while ((match = fieldRegex.exec(content)) !== null) {
    const k = match[1].trim();
    const v = match[2].trim();
    if (k && v) out[k] = v;
  }

  if (Object.keys(out).length > 0) return out;

  // Fall back to simple "Key: Value" lines
  for (const line of content.split("\n")) {
    const colonIdx = line.indexOf(":");
    if (colonIdx > 0) {
      const k = line.substring(0, colonIdx).trim();
      const v = line.substring(colonIdx + 1).trim();
      if (k && v) out[k] = v;
    }
  }

  return out;
}

export function parseReasoning(text: string): { reasoning: ReasoningData | null; cleanContent: string } {
  const raw = getReasoningContent(text);
  if (raw === null) {
    return { reasoning: null, cleanContent: text };
  }

  const parsed = parseKV(raw);
  const cleaned = stripReasoning(text);

  return {
    reasoning: Object.keys(parsed).length > 0 ? parsed : null,
    cleanContent: cleaned,
  };
}

export function formatAsBlock(data: ReasoningData, xmlStyle: boolean = false): string {
  if (xmlStyle) {
    const parts = [TAG_OPEN];
    for (const [k, v] of Object.entries(data)) {
      parts.push(`<field name="${k}">${v}</field>`);
    }
    parts.push(TAG_CLOSE);
    return parts.join("\n");
  }

  const inner = Object.entries(data)
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n");
  return `${TAG_OPEN}\n${inner}\n${TAG_CLOSE}`;
}

export function prependReasoning(text: string, reasoning: ReasoningData): string {
  const block = formatAsBlock(reasoning);
  return `${block}\n${text}`;
}
